import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { appointmentApi } from '@services/appointment.service'
import { Button } from '@components/ui/button'
import { Label } from '@components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@components/ui/dialog'
import { CalendarClock, Trash2, LayoutGrid, List, Plus } from 'lucide-react'
import { cn } from '@lib/utils'
import { useToast } from '@hooks/use-toast'
import { TIME_OPTIONS, formatTime12h } from './time-utils'

const ABS_START = 7
const ABS_END = 21
const HOUR_HEIGHT = 36

const DAY_FULL = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']
const DAY_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

interface CalendarBlock {
  id?: string
  weekday: number
  start_time: string
  end_time: string
}

interface DialogState {
  open: boolean
  mode: 'add' | 'edit'
  weekday: number
  startTime: string
  endTime: string
  windowId?: string
}

const CLOSED_DIALOG: DialogState = {
  open: false,
  mode: 'add',
  weekday: 0,
  startTime: '09:00',
  endTime: '17:00',
}

function getVisibleRange(blocks: CalendarBlock[]) {
  if (!blocks.length) return { visStart: 8, visEnd: 18 }
  const startHours = blocks.map((b) => parseInt(b.start_time))
  const endHours = blocks.map((b) => {
    const [h, m] = b.end_time.split(':').map(Number)
    return m > 0 ? h + 1 : h
  })
  return {
    visStart: Math.max(ABS_START, Math.min(...startHours) - 1),
    visEnd: Math.min(ABS_END, Math.max(...endHours) + 1),
  }
}

function makeMappers(visStart: number, visEnd: number) {
  const totalMinutes = (visEnd - visStart) * 60
  const gridH = HOUR_HEIGHT * (visEnd - visStart)
  return {
    timeToPx: (time: string) => {
      const [h, m] = time.split(':').map(Number)
      const minutes = (h - visStart) * 60 + m
      return Math.max(0, Math.min((minutes / totalMinutes) * gridH, gridH))
    },
    pxToTime: (px: number) => {
      const raw = Math.round(((Math.max(0, px) / gridH) * totalMinutes) / 30) * 30
      const capped = Math.min(raw, totalMinutes - 30)
      const h = visStart + Math.floor(capped / 60)
      const m = capped % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    },
    gridH,
  }
}

function clampAddHour(time: string): string {
  const [h, m] = time.split(':').map(Number)
  return `${String(Math.min(h + 1, ABS_END)).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

interface Props {
  doctorId: string
}

export function AvailabilityCalendarGrid({ doctorId }: Props) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [mobileDay, setMobileDay] = useState(0)
  const [dialog, setDialog] = useState<DialogState>(CLOSED_DIALOG)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')

  const { data: rule, isLoading: ruleLoading } = useQuery({
    queryKey: ['availability-rule', doctorId],
    queryFn: () => appointmentApi.getAvailabilityRule(doctorId),
  })
  const { data: weekly, isLoading: weeklyLoading } = useQuery({
    queryKey: ['weekly-availability', doctorId],
    queryFn: () => appointmentApi.getWeeklyAvailability(doctorId),
  })

  const isGranular = rule?.work_days == null

  const blocks: CalendarBlock[] = (() => {
    if (!rule) return []
    if (isGranular) {
      return (weekly?.items ?? []).map((w) => ({
        id: w.id,
        weekday: w.weekday,
        start_time: w.start_time.slice(0, 5),
        end_time: w.end_time.slice(0, 5),
      }))
    }
    return (rule.work_days ?? []).map((weekday) => ({
      weekday,
      start_time: (rule.work_start_time ?? '09:00').slice(0, 5),
      end_time: (rule.work_end_time ?? '17:00').slice(0, 5),
    }))
  })()

  const { visStart, visEnd } = getVisibleRange(blocks)
  const { timeToPx, pxToTime, gridH } = makeMappers(visStart, visEnd)
  const HOURS = Array.from({ length: visEnd - visStart }, (_, i) => i + visStart)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['availability-rule', doctorId] })
    queryClient.invalidateQueries({ queryKey: ['weekly-availability', doctorId] })
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { mode, weekday, startTime, endTime, windowId } = dialog
      if (!(startTime < endTime)) throw new Error('invalid_range')

      if (rule && !isGranular) {
        const existing = weekly?.items ?? []
        if (existing.length === 0 && rule.work_days?.length) {
          for (const day of rule.work_days) {
            await appointmentApi.createWeeklyAvailability({
              doctor_id: doctorId,
              weekday: day,
              start_time: (rule.work_start_time ?? '09:00').slice(0, 5),
              end_time: (rule.work_end_time ?? '17:00').slice(0, 5),
            })
          }
        }
        await appointmentApi.upsertAvailabilityRule(doctorId, { work_days: null })
      }

      if (mode === 'edit' && windowId) {
        await appointmentApi.deleteWeeklyAvailability(windowId)
      }
      await appointmentApi.createWeeklyAvailability({
        doctor_id: doctorId,
        weekday,
        start_time: startTime,
        end_time: endTime,
      })
    },
    onSuccess: () => {
      invalidate()
      setDialog(CLOSED_DIALOG)
      toast({ title: 'Horario guardado' })
    },
    onError: () => {
      toast({
        title: 'Verifica que la hora de inicio sea antes de la hora de fin',
        variant: 'destructive',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (windowId: string) => appointmentApi.deleteWeeklyAvailability(windowId),
    onSuccess: () => {
      invalidate()
      setDialog(CLOSED_DIALOG)
    },
  })

  const openAdd = (weekday: number, startTime = '09:00') => {
    setDialog({ open: true, mode: 'add', weekday, startTime, endTime: clampAddHour(startTime) })
  }

  const openEdit = (block: CalendarBlock) => {
    setDialog({
      open: true,
      mode: 'edit',
      weekday: block.weekday,
      startTime: block.start_time,
      endTime: block.end_time,
      windowId: block.id,
    })
  }

  const handleBodyClick = (weekday: number, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const startTime = pxToTime(e.clientY - rect.top)
    setDialog({ open: true, mode: 'add', weekday, startTime, endTime: clampAddHour(startTime) })
  }

  if (ruleLoading || weeklyLoading) {
    return (
      <div className="overflow-hidden rounded-xl border bg-card">
        <div className="h-32 animate-pulse bg-muted/40" />
      </div>
    )
  }

  const hasBlocks = blocks.length > 0

  return (
    <>
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        {/* Toolbar */}
        <div className="flex items-center justify-between border-b bg-ml-surface-container-low px-3 py-2">
          {hasBlocks ? (
            <span className="text-xs font-medium text-muted-foreground">
              Horario semanal
            </span>
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CalendarClock className="h-3.5 w-3.5 shrink-0" />
              <span>Sin horario — agrega disponibilidad por día</span>
            </div>
          )}

          <div className="flex items-center gap-0.5 rounded-lg border bg-muted/40 p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={cn(
                'flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-all',
                viewMode === 'list'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <List className="h-3 w-3" />
              Lista
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={cn(
                'flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-all',
                viewMode === 'grid'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <LayoutGrid className="h-3 w-3" />
              Calendario
            </button>
          </div>
        </div>

        {viewMode === 'list' ? (
          /* ── LIST VIEW ── */
          <div className="divide-y divide-border/40">
            {DAY_FULL.map((day, i) => {
              const dayBlocks = blocks.filter((b) => b.weekday === i)
              return (
                <div
                  key={day}
                  className="group flex items-start gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors"
                >
                  <span className="w-8 shrink-0 pt-0.5 text-xs font-semibold text-foreground/60">
                    {DAY_SHORT[i]}
                  </span>

                  <div className="flex min-h-[22px] flex-1 flex-wrap items-center gap-1.5">
                    {dayBlocks.length === 0 ? (
                      <span className="text-xs italic text-muted-foreground/50">
                        Sin disponibilidad
                      </span>
                    ) : (
                      dayBlocks.map((block, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => openEdit(block)}
                          className="rounded-md bg-[#2d7a5e]/15 px-2 py-0.5 text-xs font-medium text-[#2d7a5e] transition-colors hover:bg-[#2d7a5e]/25"
                        >
                          {formatTime12h(block.start_time)} – {formatTime12h(block.end_time)}
                        </button>
                      ))
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => openAdd(i)}
                    className="shrink-0 pt-0.5 opacity-0 transition-opacity group-hover:opacity-100 text-muted-foreground hover:text-ml-primary"
                    title={`Agregar disponibilidad — ${day}`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              )
            })}
          </div>
        ) : (
          /* ── GRID VIEW (compact) ── */
          <>
            {/* Mobile day tabs */}
            <div className="flex gap-1 border-b bg-card px-2 pt-2 pb-1 sm:hidden">
              {DAY_SHORT.map((label, i) => {
                const active = blocks.some((b) => b.weekday === i)
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setMobileDay(i)}
                    className={cn(
                      'relative flex-1 rounded-md py-1.5 text-xs font-medium transition-colors',
                      mobileDay === i
                        ? 'bg-ml-primary text-white'
                        : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {label}
                    {active && mobileDay !== i && (
                      <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-ml-primary" />
                    )}
                  </button>
                )
              })}
            </div>

            {/* Day headers — sticky above scroll */}
            <div className="flex border-b bg-ml-surface-container-low">
              <div className="w-12 shrink-0 border-r" />
              <div className="flex min-w-0 flex-1">
                {Array.from({ length: 7 }, (_, i) => i).map((weekday) => (
                  <div
                    key={weekday}
                    className={cn(
                      'flex h-8 flex-1 items-center justify-center gap-1 border-r last:border-r-0 px-1',
                      weekday !== mobileDay ? 'hidden sm:flex' : 'flex'
                    )}
                  >
                    <span className="text-[10px] font-semibold text-foreground/70">
                      {DAY_SHORT[weekday]}
                    </span>
                    {blocks.some((b) => b.weekday === weekday) && (
                      <span className="h-1.5 w-1.5 rounded-full bg-ml-primary/50" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Scrollable grid body */}
            <div className="flex max-h-[420px] overflow-y-auto">
              {/* Hour labels */}
              <div className="w-12 shrink-0 select-none border-r bg-ml-surface-container-low">
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className="relative border-b border-border/15"
                    style={{ height: HOUR_HEIGHT }}
                  >
                    <span className="absolute right-2 top-1 text-[9px] leading-none text-muted-foreground">
                      {h}:00
                    </span>
                  </div>
                ))}
              </div>

              {/* Day columns */}
              <div className="flex min-w-0 flex-1">
                {Array.from({ length: 7 }, (_, i) => i).map((weekday) => {
                  const dayBlocks = blocks.filter((b) => b.weekday === weekday)
                  return (
                    <div
                      key={weekday}
                      className={cn(
                        'flex-1 border-r last:border-r-0 min-w-0',
                        weekday !== mobileDay ? 'hidden sm:block' : 'block'
                      )}
                    >
                      <div
                        className="relative cursor-pointer"
                        style={{ height: gridH }}
                        onClick={(e) => handleBodyClick(weekday, e)}
                        title={`Agregar disponibilidad — ${DAY_FULL[weekday]}`}
                      >
                        {/* Hour grid lines */}
                        {HOURS.map((_, i) => (
                          <div
                            key={i}
                            className="pointer-events-none absolute inset-x-0 border-t border-border/20"
                            style={{ top: i * HOUR_HEIGHT }}
                          />
                        ))}
                        {/* Half-hour dashed lines */}
                        {HOURS.slice(0, -1).map((_, i) => (
                          <div
                            key={i}
                            className="pointer-events-none absolute inset-x-0 border-t border-dashed border-border/10"
                            style={{ top: i * HOUR_HEIGHT + HOUR_HEIGHT / 2 }}
                          />
                        ))}

                        {/* Availability blocks */}
                        {dayBlocks.map((block, idx) => {
                          const top = timeToPx(block.start_time)
                          const height = Math.max(timeToPx(block.end_time) - top, 16)
                          const showText = height >= 28

                          return (
                            <div
                              key={idx}
                              className="absolute inset-x-0.5 z-10 cursor-pointer rounded border border-[#2d7a5e]/30 bg-[#2d7a5e]/15 transition-colors hover:bg-[#2d7a5e]/25"
                              style={{ top, height }}
                              onClick={(e) => {
                                e.stopPropagation()
                                openEdit(block)
                              }}
                            >
                              {showText && (
                                <div className="px-1 pt-0.5">
                                  <p className="truncate text-[9px] font-semibold leading-tight text-[#2d7a5e]">
                                    {formatTime12h(block.start_time)}
                                  </p>
                                  <p className="truncate text-[9px] leading-tight text-[#2d7a5e]/70">
                                    {formatTime12h(block.end_time)}
                                  </p>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(open) => !open && setDialog(CLOSED_DIALOG)}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-base">
              {dialog.mode === 'add' ? 'Agregar disponibilidad' : 'Editar horario'}
              <span className="ml-1 font-normal text-muted-foreground">
                — {DAY_FULL[dialog.weekday]}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="space-y-2">
              <Label>Hora inicio</Label>
              <Select
                value={dialog.startTime}
                onValueChange={(v) => setDialog((prev) => ({ ...prev, startTime: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_OPTIONS.filter(
                    (o) => o.value >= `${String(ABS_START).padStart(2, '0')}:00`
                  ).map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Hora fin</Label>
              <Select
                value={dialog.endTime}
                onValueChange={(v) => setDialog((prev) => ({ ...prev, endTime: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_OPTIONS.filter((o) => o.value > dialog.startTime).map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            {dialog.mode === 'edit' && dialog.windowId && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mr-auto text-destructive hover:text-destructive"
                onClick={() => dialog.windowId && deleteMutation.mutate(dialog.windowId)}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Eliminar
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDialog(CLOSED_DIALOG)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
